import { Component, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { GoToStep, UpdateRegisterDetails } from '../../../../store/onboarding/onboarding.actions';
import { BoFormsModule } from '@bo/ng-forms';
import { AppFormComponent } from '../../../../shared/classes/app-form';
import { mergeMap, Observable, tap } from 'rxjs';
import { OnboardingSelectors } from '../../../../store/onboarding/onboarding.selectors';
import { Method } from '@bo/ng-api';
import { FieldComponent } from '../../../../shared/components/forms/form-field/form-field.component';
import { SubmitComponent } from '../../../../shared/components/forms/submit/submit.component';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha-2';
import { RegistrationDataModel } from '../../../../_interfaces/registration_response.interface';
import { Router, RouterLink } from '@angular/router';
import { OnboardingSteps } from '../../../../store/onboarding/onboarding.model';
import { RegisterService } from '../../../../_services/register-service/register.service';
import { PhoneFieldComponent } from '../../../../shared/components/forms/phone-field/phone-field.component';
import { RouterSwitchBtnComponent } from '../../../../shared/components/router-switch-btn/router-switch-btn.component';
import { IDFieldComponent } from '../../../../shared/components/forms/id-field/id-field.component';
import { Validators } from '@angular/forms';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';

@Component({
  imports: [
    BoFormsModule,
    FieldComponent,
    SubmitComponent,
    RecaptchaV3Module,
    PhoneFieldComponent,
    RouterSwitchBtnComponent,
    IDFieldComponent,
    LoaderComponent,
    RouterLink,
  ],
  templateUrl: './get-started.component.html',
})
export class GetStartedComponent extends AppFormComponent<RegistrationDataModel> implements OnDestroy {
  readonly registerService = inject(RegisterService);
  readonly store = inject(Store);
  readonly recaptchaV3Service = inject(ReCaptchaV3Service);
  readonly router = inject(Router);

  protected override fields: string[] = [
    'first_name',
    'last_name',
    'email',
    'phone',
    'idnum_type',
    'idnum',
    'marketing',
    'terms',
    'consent',
    'date_of_birth',
  ];
  protected step: OnboardingSteps = 0;
  @ViewChild('termsLabel') termsLabel?: TemplateRef<HTMLElement>;
  private reCaptcha: HTMLDivElement | null = null;

  constructor() {
    super();
    this.store.dispatch(new GoToStep(OnboardingSteps.GET_STARTED));
    this.store.select(OnboardingSelectors.step).subscribe({
      next: (res) => (this.step = res),
    });
  }

  override createForm(): void {
    this.buildFromOptions(
      this.registerService.getOptions(this.registerService.URL_SIGNUP + '/', Method.POST),
    ).subscribe({
      next: () => {
        this.fillFormFromStore();
        this.form.get('terms')?.addValidators(Validators.requiredTrue);
        this.form.get('consent')?.addValidators(Validators.requiredTrue);
      },
    });
  }

  fillFormFromStore() {
    const cachedValue = this.store.selectSnapshot(OnboardingSelectors.registerDetails);
    if (this.form && cachedValue) {
      this.form.patchValue(cachedValue);
      const submitted = cachedValue.id != undefined;
      this.form.patchValue({
        consent: submitted,
        terms: submitted,
      });
    }
  }

  override handleSubmitSuccess(): void {
    this.router.navigate(['/sign-up/business-details']);
  }

  protected override getGenericFormErrorMessage(): string {
    return 'We could not create this profile. Please review the errors below.';
  }

  override submitData(formData: RegistrationDataModel): Observable<RegistrationDataModel> {
    const data: RegistrationDataModel = formData;
    data.marketing = Boolean(data.marketing);
    return this.recaptchaV3Service.execute('signup').pipe(
      mergeMap((token) => {
        data.captcha = token;
        return this.registerService.register(data);
      }),
      tap((value) => {
        this.store.dispatch(new UpdateRegisterDetails(value));
      }),
    );
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.reCaptcha = document.querySelector('.grecaptcha-badge');
    if (this.reCaptcha) {
      this.reCaptcha.style.setProperty('visibility', 'hidden');
    }
  }
}
